import React from 'react';
import Callout from '../components/docs/Callout';
import Shot from '../components/docs/Shot';

/** Page bodies, keyed by slug. Metadata lives in registry.js. */
const C = ({ children }) => <>{children}</>;

const CONTENT = {
  'what-is-trados': () => (
    <C>
      <p>Trados is a verified network for global commodity trade. It does three things: it maintains a searchable
      directory of trading companies, it verifies who those companies actually are, and it authenticates the trade
      documents they exchange.</p>
      <h2>Why verification is the point</h2>
      <p>Anyone can list a company. What is hard — and what Trados exists to solve — is knowing whether the
      counterparty on the other side of a deal is real. Every listing can be claimed by its owner, and every owner can
      be put through a tiered identity and business check.</p>
      <Shot device="desktop" alt="The Trados company directory" caption="The directory — searchable by company, country and commodity." />
      <h2>Who it is for</h2>
      <ul>
        <li><strong>Buyers</strong> sourcing commodities who need to confirm a seller exists and is who they claim.</li>
        <li><strong>Sellers and producers</strong> who want a verifiable public profile.</li>
        <li><strong>Brokers</strong> introducing counterparties who need both sides checked.</li>
      </ul>
      <Callout type="tip">Start by <a href="/directory">finding your company in the directory</a> and claiming it.
      Verification builds on that listing.</Callout>
    </C>
  ),

  'creating-your-account': () => (
    <C>
      <p>Registration takes a minute. You need a working email address — access to contact details across the
      directory is gated behind a confirmed account.</p>
      <h2>Register</h2>
      <ol>
        <li>Go to <a href="/register">the registration page</a>.</li>
        <li>Enter your email address and choose a password.</li>
        <li>Pick your account type — buyer, seller, buyer/seller or broker.</li>
      </ol>
      <Shot device="desktop" alt="Registration form" caption="Registration — email, password and account type." />
      <h2>Verify your email</h2>
      <p>We send a verification link immediately. Clicking it confirms your address and activates your account in one
      step — there is no separate approval wait.</p>
      <Callout type="note">The link expires after 24 hours. If it lapses, sign in and request a new one from the
      prompt shown on your dashboard.</Callout>
      <h2>What an account unlocks</h2>
      <ul>
        <li>Contact details on directory listings — emails, phones and the main contact.</li>
        <li>Claiming your own company listing.</li>
        <li>Creating and verifying Letters of Intent.</li>
      </ul>
    </C>
  ),

  'account-types': () => (
    <C>
      <p>Two things are tracked separately: what you trade, and what you are allowed to administer.</p>
      <h2>Account type</h2>
      <p>Chosen at registration and shown on your profile: <strong>Buyer</strong>, <strong>Seller</strong>,
      <strong> Buyer/Seller</strong> or <strong>Broker</strong>. It describes your role in the market and can be
      changed later during verification.</p>
      <h2>Roles</h2>
      <table>
        <thead><tr><th>Role</th><th>Scope</th></tr></thead>
        <tbody>
          <tr><td><strong>User</strong></td><td>Their own data, their own company listing once claimed.</td></tr>
          <tr><td><strong>Company admin</strong></td><td>Everything above, plus their company&rsquo;s users, settings and billing.</td></tr>
          <tr><td><strong>Super admin</strong></td><td>All companies, the directory, verification queues and platform settings.</td></tr>
        </tbody>
      </table>
      <Callout type="admin">Company admins see an expanded sidebar with company settings, users and billing.
      Regular users do not.</Callout>
    </C>
  ),

  'directory-overview': () => (
    <C>
      <p>The directory lists commodity trading companies worldwide — searchable by name, country and the commodities
      they trade.</p>
      <Shot device="desktop" alt="Directory search with the world map" caption="Every listed company plotted by location." />
      <h2>Searching</h2>
      <ul>
        <li><strong>Free text</strong> matches company name, city and commodity.</li>
        <li><strong>Country</strong> narrows to a single market — the panel under the map shows counts per country.</li>
        <li><strong>Commodity</strong> filters to companies trading a specific good.</li>
      </ul>
      <h2>What you can see</h2>
      <p>Anyone can see a company&rsquo;s name, logo, location, commodities and description. Contact details —
      website, emails, phone numbers and the main contact — require a signed-in account.</p>
      <Callout type="note">Listings show a verification ribbon. &ldquo;Not Yet Trados Verified&rdquo; means the
      company has not completed verification, not that anything is wrong with it.</Callout>
    </C>
  ),

  'claiming-your-business': () => (
    <C>
      <p>Most listings were seeded from public trade data, so your company may already be listed. Claiming it makes
      you the owner and lets you manage the content.</p>
      <h2>Claim it</h2>
      <ol>
        <li>Find your company in <a href="/directory">the directory</a>.</li>
        <li>Open the listing and choose <strong>Claim this Business</strong>.</li>
        <li>If your account email domain matches the listing&rsquo;s website or email domain, the claim is approved
        automatically.</li>
      </ol>
      <Callout type="tip">Claims from a company email address (you@yourcompany.com) approve instantly. Claims from a
      free mailbox — Gmail, Outlook, Yahoo — go to a Trados admin for review.</Callout>
      <h2>Not listed yet?</h2>
      <p>Submit it from the directory page. New submissions go to the moderation queue and appear publicly once
      approved.</p>
    </C>
  ),

  'edit-business-info': () => (
    <C>
      <p>Once your claim is approved, <strong>Edit Business Info</strong> appears in your sidebar. This is your public
      listing, and it doubles as the source for your verification details.</p>
      <Shot device="desktop" alt="Edit business information form" caption="Everything on your public listing, in one form." />
      <h2>What you can change</h2>
      <ul>
        <li><strong>Company name</strong> and <strong>logo</strong>, with a background colour for the logo tile.</li>
        <li><strong>Directory address</strong> — your public URL, with a live availability check.</li>
        <li><strong>About us</strong> — paragraphs and bullet points are preserved as you type them.</li>
        <li><strong>Location</strong> — country, city and street address.</li>
        <li><strong>Contact details</strong> — visible only to signed-in users.</li>
      </ul>
      <Callout type="tip">Changing your directory address keeps the old one working. Existing links and search
      rankings follow you to the new address automatically.</Callout>
      <h2>It fills in your verification too</h2>
      <p>Company name, country, address, website and description are reused to pre-fill tier verification, so you
      enter them once. Anything you have already answered on a verification is never overwritten.</p>
    </C>
  ),

  'commodities-and-minerals': () => (
    <C>
      <p>What you trade is recorded in three fields, and the difference between them matters.</p>
      <table>
        <thead><tr><th>Field</th><th>How it works</th></tr></thead>
        <tbody>
          <tr><td><strong>Commodities</strong></td><td>Pick from a standard list of ~165 traded goods.</td></tr>
          <tr><td><strong>Minerals &amp; metals</strong></td><td>Pick from a standard list of ~120 minerals, metals and ores.</td></tr>
          <tr><td><strong>Other</strong></td><td>Free text — the only place you can type something new.</td></tr>
        </tbody>
      </table>
      <h2>Why picking from a list</h2>
      <p>If one company writes &ldquo;Soybeans&rdquo; and another writes &ldquo;Soybean&rdquo;, a buyer filtering for
      soybeans finds only half of you. Choosing from the catalogue keeps naming consistent, which is what makes
      &ldquo;show me companies selling X&rdquo; return a complete answer.</p>
      <Callout type="note">Common variants are normalised for you — &ldquo;Maize&rdquo; resolves to Corn,
      &ldquo;Soybean&rdquo; to Soybeans, and the frequent &ldquo;Icusa&rdquo; misspelling to Sugar Icumsa 45.</Callout>
      <Callout type="tip">Only use <strong>Other</strong> when nothing in either list fits. Services such as shipping
      or freight are not commodities and should not go here.</Callout>
    </C>
  ),

  'network-badge': () => (
    <C>
      <p>Verified companies can display their medal on their own website. It reads your live status from Trados each
      time the page loads.</p>
      <Shot device="desktop" alt="Network badge preview" caption="Light and dark, in two sizes." />
      <h2>Get your embed code</h2>
      <p>Open <strong>Trados Network Badge</strong> in your sidebar, choose light or dark and a size, then copy the
      snippet:</p>
      <pre><code>{`<div class="trados-network-badge" data-company="your-address"></div>
<script src="https://www.trados.app/widgets/trados-badge.js" async></script>`}</code></pre>
      <p>Paste it anywhere in your site&rsquo;s HTML — footer, about page or homepage.</p>
      <h2>It cannot be faked</h2>
      <p>The badge draws its content from Trados, not from the snippet. Copying someone else&rsquo;s code produces
      nothing, and if a verification lapses the badge disappears on its own.</p>
      <Callout type="note">Companies that are not verified render no badge at all — deliberately, rather than showing
      an empty or &ldquo;unverified&rdquo; mark.</Callout>
    </C>
  ),

  'verification-overview': () => (
    <C>
      <p>Verification runs in three tiers. Each one builds on the last, and you complete them in order.</p>
      <table>
        <thead><tr><th>Tier</th><th>What it checks</th></tr></thead>
        <tbody>
          <tr><td><strong>Bronze</strong></td><td>Who you are — name, contact details, country, identity document.</td></tr>
          <tr><td><strong>Silver</strong></td><td>Your company — legal name, incorporation country, address, company documents.</td></tr>
          <tr><td><strong>Gold</strong></td><td>Full KYC/KYB — government ID, proof of address, tax and registration documents.</td></tr>
        </tbody>
      </table>
      <h2>What it unlocks</h2>
      <p>An approved tier turns your directory listing&rsquo;s ribbon to <strong>Trados Verified</strong> with your
      medal, enables the embeddable network badge, and ranks you above unverified companies in search.</p>
      <Callout type="note">Each submission is reviewed by a Trados administrator. You will be emailed when a tier is
      approved or if something needs correcting.</Callout>
    </C>
  ),

  'tier-bronze': () => (
    <C>
      <p>Bronze establishes who you are. It is the entry point — Silver and Gold stay locked until it is approved.</p>
      <h2>What you provide</h2>
      <ul>
        <li>First and last name, and your account type.</li>
        <li>Email, phone and WhatsApp.</li>
        <li>Country — detected automatically where possible.</li>
        <li>A short description of your business.</li>
        <li>A form of identification, and optionally a business card.</li>
      </ul>
      <Callout type="tip">If you have already filled in your business information, the country, description and
      company details are pre-filled here automatically.</Callout>
    </C>
  ),

  'tier-silver-gold': () => (
    <C>
      <h2>Silver — your company</h2>
      <ul>
        <li>Legal company name and country of incorporation.</li>
        <li>Company type, address and website.</li>
        <li>Your job title, preferred language and timezone.</li>
        <li>Company documents.</li>
      </ul>
      <h2>Gold — full KYC/KYB</h2>
      <ul>
        <li>Government-issued ID and a selfie.</li>
        <li>Proof of address.</li>
        <li>Company registration and tax identification documents.</li>
      </ul>
      <Callout type="warning">Gold requires genuine identity documents. Submissions with unreadable or mismatched
      documents are rejected with a reason so you can resubmit.</Callout>
    </C>
  ),

  'create-loi': () => (
    <C>
      <p>A Letter of Intent created through Trados carries a verification record, so the counterparty can confirm it
      is genuine rather than taking a PDF at face value.</p>
      <ol>
        <li>Open <a href="/create-loi">Create LOI</a>.</li>
        <li>Fill in the buyer, seller, commodity, quantity and terms.</li>
        <li>Generate the document — it is issued with a verification reference and QR code.</li>
      </ol>
      <Shot device="desktop" alt="LOI creation wizard" caption="The LOI wizard." />
      <Callout type="tip">Send the verification link alongside the PDF. It lets the other side confirm authenticity
      without contacting you.</Callout>
    </C>
  ),

  'verify-loi': () => (
    <C>
      <p>Received an LOI and want to know whether it is real? Upload it and Trados analyses the document.</p>
      <ol>
        <li>Open <a href="/verify-loi">Verify LOI</a>.</li>
        <li>Upload the PDF, or enter the verification reference.</li>
        <li>Read the grade and the full report.</li>
      </ol>
      <h2>What the report covers</h2>
      <p>The analysis grades the document across several categories and flags anything inconsistent — altered fields,
      missing signatures, template mismatches and metadata that does not line up with the stated issuer.</p>
      <Callout type="warning">A low grade is not proof of fraud, and a high grade is not a guarantee. Treat the report
      as evidence for your own due diligence.</Callout>
    </C>
  ),

  'quote-requests': () => (
    <C>
      <p>Quote requests move a deal through defined steps, with each step reviewed before the next opens.</p>
      <ol>
        <li>Open <strong>Request a Quote</strong> and submit the details.</li>
        <li>Track it under <strong>View My Requests</strong>.</li>
        <li>Complete each process step as it unlocks — uploading documents where asked.</li>
      </ol>
      <Callout type="note">You are emailed when a step is approved, when a step needs changes, and whenever an
      administrator adds a note to your request.</Callout>
    </C>
  ),

  'admin-directory': () => (
    <C>
      <Callout type="admin">Everything on this page is restricted to super administrators.</Callout>
      <h2>Manage Directory</h2>
      <p>Create, edit and delete listings, and work the claims queue — approving a claim makes that user the owner of
      the listing.</p>
      <h2>Directory Data</h2>
      <p>A CSV-style view of every listing, one row per company. It carries statistics (total records, missing phone,
      missing emails, claimed, records per country), sortable columns, per-column filters and a CSV export.</p>
      <Shot device="desktop" alt="Directory data view" caption="Every listing, one per line, with filters and export." />
      <h2>Importing</h2>
      <p>Companies can be bulk imported from .xlsx or .csv. The importer dedupes against existing listings by website
      domain, then email, then name and country, and supports a dry run.</p>
    </C>
  ),

  'admin-verifications': () => (
    <C>
      <Callout type="admin">Restricted to super administrators.</Callout>
      <p>Tier submissions arrive in a queue with the submitted answers and any uploaded documents.</p>
      <ul>
        <li>Review the answers and open each document.</li>
        <li>Approve to grant the tier — the company&rsquo;s ribbon and badge update immediately.</li>
        <li>Reject with a reason, which is emailed to the applicant so they can correct and resubmit.</li>
      </ul>
      <Callout type="note">Verification questions are database-driven, so the form can be changed without a release.</Callout>
    </C>
  ),

  'directory-api': () => (
    <C>
      <p>An internal API for reading and writing directory listings programmatically. Authenticate with an API key
      created under <strong>Admin &rarr; API &amp; Integrations</strong>.</p>
      <pre><code>{`X-API-Key: trados_YOUR_KEY_HERE
Content-Type: application/json`}</code></pre>
      <h2>Endpoints</h2>
      <table>
        <thead><tr><th>Method</th><th>Path</th><th>Purpose</th></tr></thead>
        <tbody>
          <tr><td>GET</td><td><code>/api/agent/directory</code></td><td>Read every listing, one row per company.</td></tr>
          <tr><td>GET</td><td><code>/api/agent/directory.csv</code></td><td>The same rows as a CSV download.</td></tr>
          <tr><td>GET</td><td><code>/api/agent/directory/lookup</code></td><td>Duplicate check that writes nothing.</td></tr>
          <tr><td>POST</td><td><code>/api/agent/directory</code></td><td>Insert one listing, duplicate-guarded.</td></tr>
          <tr><td>POST</td><td><code>/api/agent/directory/bulk</code></td><td>Insert up to 500 at once.</td></tr>
          <tr><td>PUT</td><td><code>/api/agent/directory/{'{id}'}</code></td><td>Update an existing listing.</td></tr>
        </tbody>
      </table>
      <h2>Duplicate handling</h2>
      <p>Every write is checked before anything is saved, matching in order on domain, then exact email, then name and
      country. <code>on_duplicate</code> controls what happens on a match:</p>
      <ul>
        <li><code>skip</code> (default) — leave the existing listing untouched.</li>
        <li><code>update</code> — fill only the fields that are currently empty.</li>
        <li><code>replace</code> — overwrite with everything supplied.</li>
        <li><code>error</code> — return HTTP 409.</li>
      </ul>
      <Callout type="note">Filters available on the read endpoints: <code>q</code>, <code>country</code>,
      <code> source</code>, <code>claim_status</code>, <code>updated_since</code> for incremental sync, plus
      <code> limit</code> and <code>offset</code>.</Callout>
    </C>
  ),
};

export default CONTENT;
